import { Ionicons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import React, { useMemo, useState } from 'react';
import { Modal, Pressable, ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { AppText } from '@/components/ui/AppText';
import { Card } from '@/components/ui/Card';
import { useThemePalette } from '@/config/theme';
import { bzzoiroCutout, type MatchPlayer, type TeamRef } from '@/lib/data/provider';
import { localizeDigits } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { localizeTeamName } from '@/lib/i18n/footballNames';

type Slot = 0 | 1;

/** A stable identity for a player row (no provider id is guaranteed). */
function playerKey(player: MatchPlayer): string {
  return `${player.team_side}-${player.player_name}-${player.minutes}`;
}

/** Team-side accent: home = volt (cyan), away = coral. Matches the rest of the
 *  match-center so the two columns read which side each player belongs to. */
function sideColor(player: MatchPlayer, palette: ReturnType<typeof useThemePalette>): string {
  return player.team_side === 'home' ? palette.volt : palette.teamAway;
}

function oneDecimal(value: number): string {
  return (Math.round(value * 10) / 10).toFixed(1);
}
function twoDecimals(value: number): string {
  return (Math.round(value * 100) / 100).toFixed(2);
}

/** Two-letter initials from a player's name, for the no-photo fallback. */
function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase();
}

/**
 * Circular cut-out headshot with an ordered fallback chain (Bzzoiro transparent
 * cut-out → stored photo → initials). Mirrors XgPlayerRatings' PlayerPhoto but
 * kept local so the compare panel owns its sizing + ring.
 */
function CompareAvatar({
  player,
  size,
  ring,
}: {
  player: MatchPlayer;
  size: number;
  ring: string;
}) {
  const sources = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const src of [bzzoiroCutout(player.provider_player_id), player.photo_url]) {
      if (src && !seen.has(src)) {
        seen.add(src);
        out.push(src);
      }
    }
    return out;
  }, [player.provider_player_id, player.photo_url]);

  const key = sources.join('|');
  const [index, setIndex] = useState(0);
  const [seenKey, setSeenKey] = useState(key);
  if (seenKey !== key) {
    setSeenKey(key);
    setIndex(0);
  }

  const uri = index < sources.length ? sources[index] : null;
  const fontSize = Math.round(size * 0.34);

  return (
    <View
      className="items-center justify-center overflow-hidden rounded-full bg-surface-2"
      style={{ width: size, height: size, borderWidth: 1.5, borderColor: ring }}
    >
      {uri ? (
        <Image
          key={uri}
          source={{ uri }}
          style={{ width: size, height: size }}
          contentFit="cover"
          contentPosition="top"
          transition={150}
          cachePolicy="memory-disk"
          onError={() => setIndex((current) => current + 1)}
          accessibilityLabel={player.player_name}
        />
      ) : (
        <AppText weight="bold" className="text-soft" style={{ fontSize }}>
          {initialsOf(player.player_name)}
        </AppText>
      )}
    </View>
  );
}

// ---------------------------------------------------------------------------
// Stat metric model
// ---------------------------------------------------------------------------

/**
 * One comparable metric. `value` reads the raw number off a player (null when the
 * stat is missing/not applicable — pass accuracy with no passes, etc.); `format`
 * renders the localized display string. `higherIsBetter` is true for every metric
 * we surface (more goals / passes / a higher rating all win), so the winner is
 * simply the larger value — but it's explicit so the "better" highlight never has
 * to be guessed.
 */
interface Metric {
  key: string;
  label: string;
  value: (player: MatchPlayer) => number | null;
  format: (value: number, arabicNumerals: boolean) => string;
}

function buildMetrics(t: (key: string) => string): Metric[] {
  const int = (value: number, ar: boolean) => localizeDigits(String(Math.round(value)), ar);
  return [
    {
      key: 'rating',
      label: t('compare.metricRating'),
      value: (p) => p.rating,
      format: (v, ar) => localizeDigits(oneDecimal(v), ar),
    },
    {
      key: 'minutes',
      label: t('compare.metricMinutes'),
      value: (p) => p.minutes,
      format: int,
    },
    { key: 'goals', label: t('compare.metricGoals'), value: (p) => p.goals, format: int },
    { key: 'assists', label: t('compare.metricAssists'), value: (p) => p.assists, format: int },
    {
      key: 'xg',
      label: t('compare.metricXg'),
      value: (p) => p.xg,
      format: (v, ar) => localizeDigits(twoDecimals(v), ar),
    },
    {
      key: 'xa',
      label: t('compare.metricXa'),
      value: (p) => p.xa,
      format: (v, ar) => localizeDigits(twoDecimals(v), ar),
    },
    {
      key: 'shots',
      label: t('compare.metricShots'),
      value: (p) => p.total_shots,
      format: int,
    },
    {
      key: 'shotsOnTarget',
      label: t('compare.metricShotsOnTarget'),
      value: (p) => p.shots_on_target,
      format: int,
    },
    {
      key: 'keyPasses',
      label: t('compare.metricKeyPasses'),
      value: (p) => p.key_passes,
      format: int,
    },
    {
      key: 'bigChances',
      label: t('compare.metricBigChances'),
      value: (p) => p.big_chances_created,
      format: int,
    },
    {
      key: 'passes',
      label: t('compare.metricPasses'),
      value: (p) => p.total_passes,
      format: int,
    },
    {
      key: 'passAccuracy',
      label: t('compare.metricPassAccuracy'),
      value: (p) =>
        p.total_passes > 0 && p.accurate_passes != null
          ? (p.accurate_passes / p.total_passes) * 100
          : null,
      format: (v, ar) => `${localizeDigits(String(Math.round(v)), ar)}%`,
    },
  ];
}

const PLACEHOLDER = '—';

/**
 * A single comparison row: the left player's value, a centred metric label, and
 * the right player's value, with a diverging bar beneath showing each side's
 * share of the pair's total. The larger value is tinted in that player's team
 * colour and bold; ties leave both neutral. Missing values show an em-dash and
 * never "win". Laid out LTR internally so the left column is always the FIRST
 * selected player even in RTL (the two header cards above use the same order),
 * while the metric label + numerals localize normally.
 */
function CompareRow({
  metric,
  left,
  right,
  leftColor,
  rightColor,
}: {
  metric: Metric;
  left: MatchPlayer;
  right: MatchPlayer;
  leftColor: string;
  rightColor: string;
}) {
  const palette = useThemePalette();
  const { arabicNumerals } = useLocale();

  const lRaw = metric.value(left);
  const rRaw = metric.value(right);
  const lText = lRaw === null ? PLACEHOLDER : metric.format(lRaw, arabicNumerals);
  const rText = rRaw === null ? PLACEHOLDER : metric.format(rRaw, arabicNumerals);

  // Winner: the larger present value (every surfaced metric is higher-is-better).
  const lWins = lRaw !== null && (rRaw === null || lRaw > rRaw);
  const rWins = rRaw !== null && (lRaw === null || rRaw > lRaw);

  // Diverging bar shares. Both-zero / both-missing → an even, dim split so the
  // row still has a baseline rather than collapsing.
  const lNum = lRaw ?? 0;
  const rNum = rRaw ?? 0;
  const total = lNum + rNum;
  const lShare = total > 0 ? lNum / total : 0.5;
  const rShare = total > 0 ? rNum / total : 0.5;

  return (
    <View className="px-4 py-2.5" style={{ direction: 'ltr' }}>
      <View className="flex-row items-center">
        <AppText
          variant="caption"
          weight={lWins ? 'bold' : 'medium'}
          tabular
          style={{ color: lWins ? leftColor : palette.soft, flex: 1, textAlign: 'left' }}
        >
          {lText}
        </AppText>
        <AppText
          variant="micro"
          className="text-muted"
          numberOfLines={1}
          style={{ flex: 1.4, textAlign: 'center' }}
        >
          {metric.label}
        </AppText>
        <AppText
          variant="caption"
          weight={rWins ? 'bold' : 'medium'}
          tabular
          style={{ color: rWins ? rightColor : palette.soft, flex: 1, textAlign: 'right' }}
        >
          {rText}
        </AppText>
      </View>
      {/* Diverging share bar: left grows from the centre leftwards, right from the
          centre rightwards. The winning half stays at full colour; the loser dims. */}
      <View className="mt-1.5 flex-row items-center" style={{ height: 4 }}>
        <View className="flex-1 flex-row justify-end overflow-hidden rounded-l-full bg-surface-2">
          <View
            style={{
              width: `${Math.max(lShare * 100, total > 0 ? 4 : 0)}%`,
              height: 4,
              backgroundColor: leftColor,
              opacity: lWins ? 1 : 0.4,
            }}
          />
        </View>
        <View style={{ width: 1 }} />
        <View className="flex-1 flex-row justify-start overflow-hidden rounded-r-full bg-surface-2">
          <View
            style={{
              width: `${Math.max(rShare * 100, total > 0 ? 4 : 0)}%`,
              height: 4,
              backgroundColor: rightColor,
              opacity: rWins ? 1 : 0.4,
            }}
          />
        </View>
      </View>
    </View>
  );
}

/** A player header card (cut-out + name + team), tappable to swap the player in
 *  that slot. The `wins` count badge summarises who leads across the metrics. */
function CompareHeader({
  player,
  teamName,
  color,
  wins,
  onPress,
}: {
  player: MatchPlayer;
  teamName: string;
  color: string;
  wins: number;
  onPress: () => void;
}) {
  const { t } = useTranslation();
  const { arabicNumerals } = useLocale();
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={t('compare.changePlayer', { name: player.player_name })}
      onPress={onPress}
      className="flex-1 items-center gap-1.5 px-2 py-3 active:opacity-80"
    >
      <CompareAvatar player={player} size={58} ring={color} />
      <AppText
        variant="caption"
        weight="bold"
        className="text-ink"
        numberOfLines={1}
        style={{ textAlign: 'center' }}
      >
        {player.player_name}
      </AppText>
      <AppText
        variant="micro"
        className="text-muted"
        numberOfLines={1}
        style={{ textAlign: 'center' }}
      >
        {teamName}
        {player.position ? ` · ${player.position}` : ''}
      </AppText>
      <View
        className="mt-0.5 flex-row items-center gap-1 rounded-full px-2 py-0.5"
        style={{ backgroundColor: `${color}22` }}
      >
        <Ionicons name="trophy" size={11} color={color} />
        <AppText variant="micro" weight="bold" tabular style={{ color }}>
          {t('compare.winsCount', { count: wins, n: localizeDigits(String(wins), arabicNumerals) })}
        </AppText>
      </View>
    </Pressable>
  );
}

/** A selectable player row inside the picker sheet. */
function PickerRow({
  player,
  color,
  selected,
  disabled,
  onPress,
}: {
  player: MatchPlayer;
  color: string;
  selected: boolean;
  disabled: boolean;
  onPress: () => void;
}) {
  const palette = useThemePalette();
  const { arabicNumerals } = useLocale();
  const rating = player.rating;
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ selected, disabled }}
      onPress={disabled ? undefined : onPress}
      className={`flex-row items-center gap-3 rounded-card px-3 py-2.5 ${
        selected ? 'bg-volt-glass' : 'active:bg-surface-2/50'
      }`}
      style={{ opacity: disabled ? 0.35 : 1, borderWidth: 1, borderColor: selected ? color : 'transparent' }}
    >
      <CompareAvatar player={player} size={38} ring={color} />
      <View className="flex-1">
        <AppText variant="caption" weight="semibold" className="text-ink" numberOfLines={1}>
          {player.player_name}
        </AppText>
        {player.position ? (
          <AppText variant="micro" className="text-muted" numberOfLines={1}>
            {player.position}
          </AppText>
        ) : null}
      </View>
      {rating != null ? (
        <AppText variant="caption" weight="bold" tabular style={{ color: palette.soft }}>
          {localizeDigits(oneDecimal(rating), arabicNumerals)}
        </AppText>
      ) : null}
      {selected ? <Ionicons name="checkmark-circle" size={18} color={color} /> : null}
    </Pressable>
  );
}

/**
 * The player picker bottom-sheet. Lists every rated player split into Home / Away
 * sections (best rating first). Tapping a player assigns them to the open slot;
 * the OTHER slot's player is shown disabled so the same player can't fill both.
 */
function PickerSheet({
  open,
  slot,
  players,
  homeTeam,
  awayTeam,
  otherKey,
  onSelect,
  onClose,
}: {
  open: boolean;
  slot: Slot;
  players: MatchPlayer[];
  homeTeam: TeamRef | null;
  awayTeam: TeamRef | null;
  otherKey: string | null;
  onSelect: (player: MatchPlayer) => void;
  onClose: () => void;
}) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const insets = useSafeAreaInsets();

  const sections = useMemo(() => {
    const bySide = (side: 'home' | 'away') =>
      players
        .filter((p) => p.team_side === side)
        .sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0) || b.minutes - a.minutes);
    return [
      { side: 'home' as const, title: localizeTeamName(homeTeam, locale), rows: bySide('home') },
      { side: 'away' as const, title: localizeTeamName(awayTeam, locale), rows: bySide('away') },
    ].filter((section) => section.rows.length > 0);
  }, [players, homeTeam, awayTeam, locale]);

  return (
    <Modal visible={open} animationType="slide" transparent onRequestClose={onClose}>
      <Pressable className="flex-1 justify-end bg-black/60" onPress={onClose}>
        <Pressable
          onPress={(event) => event.stopPropagation()}
          className="max-h-[82%] rounded-t-3xl border border-line bg-surface"
          style={{ paddingBottom: insets.bottom + 12 }}
        >
          <View className="items-center pt-3">
            <View className="h-1 w-10 rounded-full bg-line" />
          </View>
          <View className="flex-row items-center justify-between px-5 pb-2 pt-3">
            <AppText variant="heading" weight="bold" className="text-ink">
              {t('compare.pickTitle', { slot: slot + 1 })}
            </AppText>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t('common.close')}
              hitSlop={10}
              onPress={onClose}
            >
              <Ionicons name="close" size={22} color={palette.muted} />
            </Pressable>
          </View>
          <ScrollView contentContainerStyle={{ paddingHorizontal: 12, paddingBottom: 8 }}>
            {sections.map((section) => {
              const color = section.side === 'home' ? palette.volt : palette.teamAway;
              return (
                <View key={section.side} className="mb-2">
                  <AppText
                    variant="micro"
                    weight="bold"
                    className="px-2 pb-1 pt-2 uppercase tracking-widest text-muted"
                    numberOfLines={1}
                  >
                    {section.title}
                  </AppText>
                  {section.rows.map((player) => {
                    const key = playerKey(player);
                    return (
                      <PickerRow
                        key={key}
                        player={player}
                        color={color}
                        selected={false}
                        disabled={key === otherKey}
                        onPress={() => onSelect(player)}
                      />
                    );
                  })}
                </View>
              );
            })}
          </ScrollView>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

/**
 * PLAYER vs PLAYER comparison. Collapsed, it's a single "Compare players"
 * affordance. Expanded, it shows two player headers (cut-out + name + team) and a
 * list of stat rows (rating, goals, assists, xG, xA, shots, key passes, passes,
 * pass accuracy…), each row tinting the BETTER value in that player's team colour
 * with a diverging share bar; a missing stat shows an em-dash and never wins.
 * Either header is tappable to swap that slot via a player picker sheet. Defaults
 * to the two highest-rated players across both teams.
 *
 * Renders nothing unless at least two players carry a rating (so the affordance
 * never appears on a match with no comparable data). Fed the same MatchPlayer[]
 * as XgPlayerRatings; no network of its own.
 */
export function PlayerComparePanel({
  players,
  homeTeam,
  awayTeam,
}: {
  players: MatchPlayer[];
  homeTeam: TeamRef | null;
  awayTeam: TeamRef | null;
}) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { locale } = useLocale();

  // Only rated players are comparable; sorted best-first so the defaults are the
  // two standout performers across the match.
  const rated = useMemo(
    () =>
      players
        .filter((p) => p.rating != null)
        .sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0) || b.minutes - a.minutes),
    [players],
  );

  const [open, setOpen] = useState(false);
  // The two compared players, by stable key. Default to the top two rated.
  const [leftKey, setLeftKey] = useState<string | null>(null);
  const [rightKey, setRightKey] = useState<string | null>(null);
  // Which slot the picker is editing, or null when closed.
  const [editing, setEditing] = useState<Slot | null>(null);

  const byKey = useMemo(() => {
    const map = new Map<string, MatchPlayer>();
    for (const p of rated) map.set(playerKey(p), p);
    return map;
  }, [rated]);

  const metrics = useMemo(() => buildMetrics(t), [t]);

  if (rated.length < 2) return null;

  // Resolve the two slots, falling back to the default top-two when a key is unset
  // or no longer present (e.g. data refreshed and a row's minute changed).
  const left = (leftKey && byKey.get(leftKey)) || rated[0];
  const right =
    (rightKey && byKey.get(rightKey)) || (rated[1] !== left ? rated[1] : rated[0]);

  const leftColor = sideColor(left, palette);
  // Two players from the SAME team resolve to the same side colour, which would
  // collapse the per-column colour cue. Force a distinct accent on the right.
  const rawRight = sideColor(right, palette);
  const rightColor =
    rawRight === leftColor
      ? leftColor === palette.volt
        ? palette.teamAway
        : palette.volt
      : rawRight;

  // Headline: how many metrics each side leads (em-dash / ties count for neither).
  const { leftWins, rightWins } = metrics.reduce(
    (acc, metric) => {
      const l = metric.value(left);
      const r = metric.value(right);
      if (l !== null && (r === null || l > r)) acc.leftWins += 1;
      else if (r !== null && (l === null || r > l)) acc.rightWins += 1;
      return acc;
    },
    { leftWins: 0, rightWins: 0 },
  );

  const selectInto = (slot: Slot, player: MatchPlayer) => {
    const key = playerKey(player);
    if (slot === 0) {
      if (key === rightKey) setRightKey(leftKey ?? playerKey(left));
      setLeftKey(key);
    } else {
      if (key === leftKey) setLeftKey(rightKey ?? playerKey(right));
      setRightKey(key);
    }
    setEditing(null);
  };

  // Collapsed: a single premium affordance that opens the comparison.
  if (!open) {
    return (
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t('compare.openLabel')}
        onPress={() => setOpen(true)}
        className="active:opacity-90"
      >
        <Card className="flex-row items-center gap-3 px-4 py-3.5">
          <View
            className="h-9 w-9 items-center justify-center rounded-full"
            style={{ backgroundColor: palette.voltGlassStrong }}
          >
            <Ionicons name="swap-horizontal" size={18} color={palette.voltBright} />
          </View>
          <View className="flex-1">
            <AppText variant="caption" weight="bold" className="text-ink">
              {t('compare.openTitle')}
            </AppText>
            <AppText variant="micro" className="text-muted" numberOfLines={1}>
              {t('compare.openSubtitle')}
            </AppText>
          </View>
          <Ionicons name="chevron-forward" size={16} color={palette.muted} />
        </Card>
      </Pressable>
    );
  }

  return (
    <View className="gap-2">
      <View className="flex-row items-center justify-between px-1">
        <AppText variant="micro" weight="bold" className="uppercase tracking-widest text-muted">
          {t('compare.title')}
        </AppText>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t('compare.collapse')}
          hitSlop={8}
          onPress={() => setOpen(false)}
          className="flex-row items-center gap-1 active:opacity-70"
        >
          <Ionicons name="chevron-up" size={14} color={palette.muted} />
          <AppText variant="micro" weight="medium" className="text-muted">
            {t('compare.collapse')}
          </AppText>
        </Pressable>
      </View>

      <Card className="overflow-hidden">
        {/* Two tappable headers, split by a centre VS divider. */}
        <View className="flex-row items-stretch" style={{ direction: 'ltr' }}>
          <CompareHeader
            player={left}
            teamName={localizeTeamName(left.team_side === 'home' ? homeTeam : awayTeam, locale)}
            color={leftColor}
            wins={leftWins}
            onPress={() => setEditing(0)}
          />
          <View className="items-center justify-center px-1">
            <View
              className="h-7 w-7 items-center justify-center rounded-full border border-line bg-surface-2"
            >
              <AppText variant="micro" weight="bold" className="text-soft">
                {t('compare.vs')}
              </AppText>
            </View>
          </View>
          <CompareHeader
            player={right}
            teamName={localizeTeamName(right.team_side === 'home' ? homeTeam : awayTeam, locale)}
            color={rightColor}
            wins={rightWins}
            onPress={() => setEditing(1)}
          />
        </View>

        {/* Stat rows. */}
        <View className="border-t border-line">
          {metrics.map((metric, index) => (
            <View key={metric.key} className={index === 0 ? '' : 'border-t border-line'}>
              <CompareRow
                metric={metric}
                left={left}
                right={right}
                leftColor={leftColor}
                rightColor={rightColor}
              />
            </View>
          ))}
        </View>
      </Card>

      <AppText variant="micro" className="px-1 text-muted" numberOfLines={2}>
        {t('compare.tapHint')}
      </AppText>

      <PickerSheet
        open={editing !== null}
        slot={editing ?? 0}
        players={rated}
        homeTeam={homeTeam}
        awayTeam={awayTeam}
        otherKey={editing === 0 ? playerKey(right) : playerKey(left)}
        onSelect={(player) => {
          if (editing !== null) selectInto(editing, player);
        }}
        onClose={() => setEditing(null)}
      />
    </View>
  );
}
